import { singleQuoteRegex, SCOOKIE_EXP_TIME_IN_SECS } from '../util/constants'
import { isObject, safeJSONParse } from '../util/datatypes'
import { getNow } from '../util/datetime'
import { $ct } from '../util/storage'
import { getHostName } from '../util/url'
import { getCampaignObject, saveCampaignObject } from '../util/clevertap'

export default class SessionManager {
  #logger
  #sessionId
  #isPersonalisationActive
  cookieName // SCOOKIE_NAME
  scookieObj
  #domainSpecification
  #storageManager
  #instanceManager

  constructor ({
    logger,
    isPersonalisationActive,
    domainSpecification,
    storageManager,
    instanceManager
  }) {
    this.domainSpecification = domainSpecification
    this.#storageManager = storageManager
    this.#instanceManager = instanceManager
    this.sessionId = this.#storageManager.getMetaProp('cs')
    this.#logger = logger
    this.#isPersonalisationActive = isPersonalisationActive
  }

  get sessionId () {
    return this.#sessionId
  }

  set sessionId (sessionId) {
    this.#sessionId = sessionId
  }

  get domainSpecification () {
    return this.#domainSpecification
  }

  set domainSpecification (domainSpecification) {
    this.#domainSpecification = domainSpecification
  }

  getSessionCookieObject () {
    let scookieStr = this.#storageManager.readCookie(this.cookieName)
    let obj = {}

    if (scookieStr != null) {
      try {
        // converting back single quotes to double for JSON parsing - http://www.iandevlin.com/blog/2012/04/html5/cookies-json-localstorage-and-opera
        scookieStr = scookieStr.replace(singleQuoteRegex, '"')

        // Use safe JSON parsing to prevent injection attacks
        obj = safeJSONParse(scookieStr, {})
        if (!isObject(obj)) {
          obj = {}
        } else {
          if (typeof obj.t !== 'undefined') { // check time elapsed since last request
            const lastTime = obj.t
            const now = getNow()
            if ((now - lastTime) > (SCOOKIE_EXP_TIME_IN_SECS + 60)) {
              // adding 60 seconds to compensate for in-journey requests
              // ideally the cookie should've died after SCOOKIE_EXP_TIME_IN_SECS but it's still around as we can read
              // hence we shouldn't use it.
              obj = {}
            }
          }
        }
      } catch (e) {
        obj = {}
      }
    }
    this.scookieObj = obj
    return obj
  }

  setSessionCookieObject (obj) {
    const objStr = JSON.stringify(obj)
    this.#storageManager.createBroadCookie(this.cookieName, objStr, SCOOKIE_EXP_TIME_IN_SECS, getHostName(), this.domainSpecification)
  }

  manageSession (session) {
    // first time. check if current session id in localstorage is same
    // if not same then prev = current and current = this new session
    if (typeof this.sessionId === 'undefined' || this.sessionId !== session) {
      const currentSessionInLS = this.#storageManager.getMetaProp('cs')
      // if sessionId in meta is undefined - set current to both
      if (typeof currentSessionInLS === 'undefined') {
        this.#storageManager.setMetaProp('ps', session)
        this.#storageManager.setMetaProp('cs', session)
        this.#storageManager.setMetaProp('sc', 1)
      } else if (currentSessionInLS !== session) {
        // not same as session in local storage. new session
        this.#storageManager.setMetaProp('ps', currentSessionInLS)
        this.#storageManager.setMetaProp('cs', session)
        let sessionCount = this.#storageManager.getMetaProp('sc')
        if (typeof sessionCount === 'undefined') {
          sessionCount = 0
        }
        this.#storageManager.setMetaProp('sc', sessionCount + 1)

        // Clear discarded events list on new session
        if (this.#instanceManager) {
          this.#instanceManager.state.discardedEventsList = null
        } else {
          $ct.discardedEventsList = null
        }

        // Reset session-based campaign counters on new session
        this.#resetSessionCampaignCounters()
      }
      this.sessionId = session
    }
  }

  #resetSessionCampaignCounters () {
    try {
      const campaignObj = getCampaignObject()
      if (campaignObj) {
        // Reset Web Popup Show Count
        if (typeof campaignObj.wsc !== 'undefined') {
          campaignObj.wsc = 0
          this.#logger.debug('Reset wsc (Web Popup Show Count) to 0 for new session')
        }

        // Reset Web Native Display Show Count
        if (typeof campaignObj.wndsc !== 'undefined') {
          campaignObj.wndsc = 0
          this.#logger.debug('Reset wndsc (Web Native Display Show Count) to 0 for new session')
        }

        saveCampaignObject(campaignObj)
      }
    } catch (error) {
      this.#logger.error('Failed to reset session campaign counters: ' + error.message)
    }
  }

  getTimeElapsed () {
    if (!this.#isPersonalisationActive()) {
      return
    }
    if (this.scookieObj != null) { // TODO: check logic?
      this.scookieObj = this.getSessionCookieObject()
    }
    const sessionStart = this.scookieObj.s
    if (sessionStart != null) {
      const ts = getNow()
      return Math.floor(ts - sessionStart)
    }
  }

  getPageCount () {
    if (!this.#isPersonalisationActive()) {
      return
    }

    if (this.scookieObj != null) { // TODO: check logic
      this.scookieObj = this.getSessionCookieObject()
    }
    return this.scookieObj.p
  }
}
